import { Link } from 'react-router-dom';
import { BadgeCheck } from 'lucide-react';
import type { OrgBrand } from '@ready/shared';
import { apiUrl } from '@/lib/api';

const SIZES = {
  xs: 'size-4 rounded text-[0.5rem]',
  sm: 'size-6 rounded-md text-[0.6rem]',
  md: 'size-9 rounded-lg text-xs',
  lg: 'size-14 rounded-xl text-base',
  xl: 'size-20 rounded-2xl text-xl sm:size-24',
} as const;

/** Placeholder colors, picked from the org's name so each org keeps its own. */
const TINTS = [
  'bg-primary/15 text-primary',
  'bg-india-green/15 text-india-green',
  'bg-saffron/15 text-saffron',
  'bg-chakra-navy/15 text-foreground',
];

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter((w) => /^[\p{L}\p{N}]/u.test(w) && !/^(of|the|and|for|&)$/i.test(w))
    .slice(0, 2)
    .map((w) => [...w][0]!.toUpperCase())
    .join('') || '?';

/**
 * An organization's logo (square, on white so dark and transparent logos
 * show in dark mode), or its initials when it has none.
 */
export function OrgLogo({
  name,
  url,
  size = 'md',
  className = '',
}: {
  name: string;
  url: string | null | undefined;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const box = `${SIZES[size]} shrink-0 overflow-hidden ${className}`;
  if (url)
    return (
      <img
        src={apiUrl(url)}
        alt=""
        loading="lazy"
        className={`${box} border border-border bg-white object-cover`}
      />
    );
  const tint = TINTS[[...name].reduce((n, ch) => n + ch.codePointAt(0)!, 0) % TINTS.length];
  return (
    <span aria-hidden className={`${box} ${tint} flex items-center justify-center font-semibold`}>
      {initials(name)}
    </span>
  );
}

export function VerifiedBadge({ className = '' }: { className?: string }) {
  return (
    <BadgeCheck
      className={`size-4 shrink-0 text-primary ${className}`}
      aria-label="Verified organization"
      role="img"
    />
  );
}

/**
 * "Offered by" an org: logo, name and badge, linking to its profile when
 * the profile is public. Nirvik's own content passes null and shows nothing.
 */
export function OfferedBy({
  org,
  label = 'Offered by',
  className = '',
}: {
  org: OrgBrand | null | undefined;
  label?: string;
  className?: string;
}) {
  if (!org) return null;
  const name = (
    <>
      <span className="truncate font-medium text-foreground">{org.name}</span>
      {org.verified && <VerifiedBadge />}
    </>
  );
  return (
    <p className={`flex min-w-0 items-center gap-2 text-sm text-muted-foreground ${className}`}>
      <OrgLogo name={org.name} url={org.logoUrl} size="sm" />
      <span className="shrink-0">{label}</span>
      {org.profilePublic ? (
        <Link
          to={`/o/${org.slug}`}
          className="-my-2 flex min-w-0 items-center gap-1 py-2 hover:underline"
        >
          {name}
        </Link>
      ) : (
        <span className="flex min-w-0 items-center gap-1">{name}</span>
      )}
    </p>
  );
}
